/**
 * Generic module-scoped registry of preload promises keyed by federation
 * package id (the SSOT identifier from src/config/federation-package-ids.js).
 *
 * Replaces the prior `window.__pkg_resolved_url__` globals: (1) removes the
 * writable global that any script could tamper with to silence drift
 * detection, (2) removes test-isolation leakage across describe blocks,
 * (3) lets drift detectors await the resolved URL instead of racing an
 * unset global. New federated packages opt in by calling
 * `setPreloadPromise(packageId, ...)` - no per-package helpers needed.
 */

const preloadPromises = new Map<string, Promise<string | null>>();

// Side-channel for SRI hashes resolved from the manifest. Decoupled from the
// promise registry above so existing call sites (drift detector, dynamic
// remote install) keep working without signature changes - only callers
// that opt into integrity (loadRemoteEntryScript) need to consult it.
const resolvedIntegrity = new Map<string, string>();

export function setPreloadPromise(packageId: string, p: Promise<string | null>): void {
  preloadPromises.set(packageId, p);
}

/**
 * Read the resolved URL for `packageId`. Returns `Promise.resolve(null)` when
 * no preload has been registered.
 *
 * **Rejection contract**: the stored promise CAN reject - `resolveRemoteUrl`
 * throws `ManifestTamperError` on commit-pin mismatch, malformed integrity,
 * disallowed `remoteEntry` URL, or missing pin in fail-closed environments.
 * Consumers MUST handle rejection (typically `.catch(() => null)` if the
 * intent is "treat tamper as not-resolved"). The registry deliberately
 * preserves the rejection so the boundary owner (`preloadAbaxUi`) can flip
 * `__ABAX_UI_LOAD_FAILED__` - masking rejection here would silently disable
 * the H1 tamper-detection contract.
 */
export function getResolvedUrl(packageId: string): Promise<string | null> {
  return preloadPromises.get(packageId) ?? Promise.resolve(null);
}

/**
 * Record the SRI hash extracted from the manifest for `packageId`. Called by
 * resolveRemoteUrl when manifest.integrity is present; consumed by
 * loadRemoteEntryScript to set `script.integrity` on the injected `<script>`.
 */
export function setResolvedIntegrity(packageId: string, integrity: string): void {
  resolvedIntegrity.set(packageId, integrity);
}

/**
 * Look up the SRI hash for `packageId`. Returns undefined when no manifest
 * was used or the manifest didn't carry an integrity field - callers must
 * fall back to non-SRI script loading without rejecting.
 */
export function getResolvedIntegrity(packageId: string): string | undefined {
  return resolvedIntegrity.get(packageId);
}

// Test helper: clear all registered promises. Production code should never
// call this - it's only for jest afterEach cleanup. Kept as `__internal_*`
// so tree-shaking and consumer search make the misuse risk obvious.
// eslint-disable-next-line @typescript-eslint/naming-convention
export function __internal_resetPreloadPromisesForTests(): void {
  preloadPromises.clear();
  resolvedIntegrity.clear();
}
